import { Badge, Card, Collapse, Divider, Drawer, Fade, IconButton, InputAdornment, InputBase, Paper, Stack, styled, useTheme } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { TFunction } from 'i18next';
import { DomEvent } from 'leaflet';
import { FC, KeyboardEvent as ReactKeyboardEvent, useEffect, useRef, type JSX } from 'react';
import { useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_WIDTH } from '../../constants';
import { useCustomRoutes, useSidebarPinContext, useSmallScreen } from '../../hooks';
import { useSearch } from '../../pages/list/use-search';
import { Icon } from '../icons';

interface Props {
  disableFilter: boolean;
  filterDrawerContent: JSX.Element;
  filtersDesktop?: JSX.Element;
  filtersLength: number;
  filtersMobile?: JSX.Element;
  handleInputFocus?: () => void;
  isInlayMap?: boolean;
  listContent?: JSX.Element;
  onKeyDown?: (event: ReactKeyboardEvent) => void;
  onKeyUp?: (event: ReactKeyboardEvent) => void;
  openFilter: boolean;
  setFilterOpen: (value: boolean) => void;
  tFunction: TFunction;
}

interface StyledDrawerProps {
  isPinned: boolean;
}

interface StyledPaperProps {
  isInlayMap?: boolean;
  smallScreen: boolean;
}

const StyledCollapse = styled(Collapse)({
  '.MuiCollapse-wrapperInner': {
    alignItems: 'center',
    display: 'flex',
  },
});

const StyledDrawer = styled(Drawer)<StyledDrawerProps>(({ theme, isPinned }) => ({
  width: '350px',
  [theme.breakpoints.down('sm')]: {
    width: '100vw',
  },
  zIndex: 1300,
  '& > .MuiPaper-root': {
    height: '100vh',
    marginLeft: isPinned ? `${SIDEBAR_FULL_WIDTH}px` : `${SIDEBAR_WIDTH}px`, // TODO: TP-7045 get width from theme
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
    },
    overflow: 'auto',
    width: 'inherit',
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(2),
}));

interface StyledPaperProps {
  isInlayMap?: boolean;
  smallScreen: boolean;
}

const StyledPaper = styled(Paper)<StyledPaperProps>(({ theme, isInlayMap, smallScreen }) => ({
  alignItems: 'center',
  alignSelf: 'flex-start',
  borderRadius: theme.shape.borderRadius * 3,
  display: 'flex',
  margin: (smallScreen || isInlayMap) ? theme.spacing(1) : theme.spacing(3),
  position: 'inherit',
  width: smallScreen ? 200 : isInlayMap ? '100%' : 400,
  zIndex: 1000,
  [theme.breakpoints.down('sm')]: {
    flex: 1,
    maxWidth: '100%',
    minWidth: 'unset',
  },
}));

const Layout: FC<Props> = ({
  disableFilter,
  filterDrawerContent,
  filtersDesktop,
  filtersLength,
  filtersMobile,
  handleInputFocus,
  isInlayMap,
  listContent,
  onKeyDown,
  onKeyUp,
  openFilter,
  setFilterOpen,
  tFunction,
}) => {
  const { text, setText } = useSearch();
  const { isPinned } = useSidebarPinContext();
  const ref = useRef<HTMLDivElement>(null);
  const smallScreen = useSmallScreen();
  const navigate = useNavigate();
  const map = useMap();
  const { things } = useCustomRoutes();
  const theme = useTheme();
  const handleClear = (): void => {
    setText('');
  };

  useEffect(() => {
    if (!ref.current) return;
    DomEvent.disableClickPropagation(ref.current);
  }, [ref]);

  return <>
    <StyledDrawer
      anchor="left"
      onClose={() => setFilterOpen(false)}
      open={openFilter}
      isPinned={isPinned}
    >
      {filterDrawerContent}
    </StyledDrawer>
    <Stack
      id="search"
      direction="row-reverse"
      justifyContent="space-between" width="100%"
    >
      <StyledPaper
        component="div"
        elevation={isInlayMap ? 0 : 3}
        isInlayMap={isInlayMap}
        smallScreen={smallScreen}
      >
        <Stack ref={ref} flex={[1, 1]} style={{ cursor: 'default' }} width="100%">
          <Card elevation={0} variant={isInlayMap ? 'outlined' : 'elevation'} style={{ display: 'flex', borderRadius: theme.spacing(3), flexDirection: 'column', margin: (smallScreen && !isInlayMap) ? theme.spacing(1) : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', my: !smallScreen ? 0.5 : 0 }}>
              <StyledInputBase
                data-testid="map-search-input-string"
                inputProps={{ 'aria-label': 'search maps' }}
                onChange={(e) => setText?.(e.target.value)}
                onFocus={() => handleInputFocus?.()}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                onTouchEnd={() => smallScreen && handleInputFocus?.()}
                placeholder={tFunction('common:component.search.labels.maps')}
                value={text}
                startAdornment={
                  <InputAdornment position="start" data-testid="map-search-button">
                    <Box display='flex' alignItems='center' fontSize='1.25rem' color={theme.palette.text.disabled}><Icon icon="Search" fontSize='inherit' data-testid="map-search-icon" /></Box>
                  </InputAdornment>
                }
              />
              {text
                && <IconButton aria-label="clear" data-testid="map-clear-button" onClick={handleClear}>
                  <Box display='flex' alignItems='center' fontSize='1.25rem' color={theme.palette.text.disabled}><Icon icon="X" style={{ fontSize: '1.25rem' }} data-testid="map-clear-icon" /></Box>
                </IconButton>
              }
              <StyledCollapse
                in={!disableFilter}
                orientation="horizontal"
              >
                <Box m={0.5} height={28}>
                  <Divider orientation='vertical' />
                </Box>
                <Box mr={1}>
                  <IconButton
                    data-testid="map-filters-button"
                    aria-label="filters"
                    size='small'
                    onClick={() => {
                      setFilterOpen(true);
                      map.fire('spiderfy');
                      navigate(`/map/${things}`, {
                        state: {
                          shouldFly: false,
                        },
                      });
                    }}
                  >
                    <Badge content={filtersLength} color='primary' hidden={!filtersLength}>
                      <Box display='flex' alignItems='center' color={theme.palette.text.disabled}>
                        <Icon fontSize='1.25rem' data-testid="filter-button" icon="Filter" />
                      </Box>
                    </Badge>
                  </IconButton>
                </Box>
              </StyledCollapse>
            </Box >
          </Card >
          <Fade in={!disableFilter} unmountOnExit>
            <div>{filtersMobile}</div>
          </Fade>
          {listContent}
        </Stack >
      </StyledPaper >
      {
        !smallScreen
        && <Fade in={!disableFilter} unmountOnExit>
          <Box sx={{ height: 'fit-content', zIndex: 400 }}>
            {filtersDesktop}
          </Box>
        </Fade>
      }
    </Stack >
  </>;
};

export default Layout;
